import moment from 'moment';

/**
 * Check if a given date is a US federal holiday
 * @param {Date|moment|string} date - The date to check (defaults to current date)
 * @returns {Object} - { isHoliday: boolean, holidayName: string|null }
 */
export const isUSHoliday = (date = new Date()) => {
  const checkDate = moment(date);
  const year = checkDate.year();
  const month = checkDate.month() + 1; // moment months are 0-indexed
  const day = checkDate.date();

  // Helper function to get nth weekday of month
  const getNthWeekdayOfMonth = (year, month, weekday, n) => {
    const firstDay = moment([year, month - 1, 1]);
    const firstWeekday = firstDay.day();
    const offset = (weekday - firstWeekday + 7) % 7;
    return firstDay.add(offset + (n - 1) * 7, 'days');
  };

  // Helper function to get last weekday of month
  const getLastWeekdayOfMonth = (year, month, weekday) => {
    const lastDay = moment([year, month - 1]).endOf('month');
    const lastWeekday = lastDay.day();
    const offset = (lastWeekday - weekday + 7) % 7;
    return lastDay.subtract(offset, 'days');
  };

  // Fixed date holidays
  const fixedHolidays = [
    { month: 1, day: 1, name: "New Year's Day" },
    { month: 7, day: 4, name: "Independence Day" },
    { month: 11, day: 11, name: "Veterans Day" },
    { month: 12, day: 25, name: "Christmas Day" }
  ];

  // Check fixed holidays
  for (const holiday of fixedHolidays) {
    if (month === holiday.month && day === holiday.day) {
      return { isHoliday: true, holidayName: holiday.name };
    }
  }

  // Variable date holidays

  // Martin Luther King Jr. Day - 3rd Monday in January
  const mlkDay = getNthWeekdayOfMonth(year, 1, 1, 3);
  if (checkDate.isSame(mlkDay, 'day')) {
    return { isHoliday: true, holidayName: "Martin Luther King Jr. Day" };
  }

  // Presidents' Day - 3rd Monday in February
  const presidentsDay = getNthWeekdayOfMonth(year, 2, 1, 3);
  if (checkDate.isSame(presidentsDay, 'day')) {
    return { isHoliday: true, holidayName: "Presidents' Day" };
  }

  // Memorial Day - Last Monday in May
  const memorialDay = getLastWeekdayOfMonth(year, 5, 1);
  if (checkDate.isSame(memorialDay, 'day')) {
    return { isHoliday: true, holidayName: "Memorial Day" };
  }

  // Labor Day - 1st Monday in September
  const laborDay = getNthWeekdayOfMonth(year, 9, 1, 1);
  if (checkDate.isSame(laborDay, 'day')) {
    return { isHoliday: true, holidayName: "Labor Day" };
  }

  // Columbus Day - 2nd Monday in October
  const columbusDay = getNthWeekdayOfMonth(year, 10, 1, 2);
  if (checkDate.isSame(columbusDay, 'day')) {
    return { isHoliday: true, holidayName: "Columbus Day" };
  }

  // Thanksgiving - 4th Thursday in November
  const thanksgiving = getNthWeekdayOfMonth(year, 11, 4, 4);
  if (checkDate.isSame(thanksgiving, 'day')) {
    return { isHoliday: true, holidayName: "Thanksgiving Day" };
  }

  return { isHoliday: false, holidayName: null };
};

/**
 * Check if today is a US federal holiday
 * @returns {Object} - { isHoliday: boolean, holidayName: string|null }
 */
export const isTodayUSHoliday = () => {
  return isUSHoliday(new Date());
};

/**
 * Get all US federal holidays for a given year
 * @param {number} year - The year to get holidays for (defaults to current year)
 * @returns {Array} - Array of holiday objects with date and name
 */
export const getUSHolidaysForYear = (year = new Date().getFullYear()) => {
  const holidays = [];

  // Helper function to get nth weekday of month
  const getNthWeekdayOfMonth = (year, month, weekday, n) => {
    const firstDay = moment([year, month - 1, 1]);
    const firstWeekday = firstDay.day();
    const offset = (weekday - firstWeekday + 7) % 7;
    return firstDay.add(offset + (n - 1) * 7, 'days');
  };

  // Helper function to get last weekday of month
  const getLastWeekdayOfMonth = (year, month, weekday) => {
    const lastDay = moment([year, month - 1]).endOf('month');
    const lastWeekday = lastDay.day();
    const offset = (lastWeekday - weekday + 7) % 7;
    return lastDay.subtract(offset, 'days');
  };

  // Add fixed holidays
  holidays.push(
    { date: moment([year, 0, 1]), name: "New Year's Day" },
    { date: moment([year, 6, 4]), name: "Independence Day" },
    { date: moment([year, 10, 11]), name: "Veterans Day" },
    { date: moment([year, 11, 25]), name: "Christmas Day" }
  );

  // Add variable holidays
  holidays.push(
    { date: getNthWeekdayOfMonth(year, 1, 1, 3), name: "Martin Luther King Jr. Day" },
    { date: getNthWeekdayOfMonth(year, 2, 1, 3), name: "Presidents' Day" },
    { date: getLastWeekdayOfMonth(year, 5, 1), name: "Memorial Day" },
    { date: getNthWeekdayOfMonth(year, 9, 1, 1), name: "Labor Day" },
    { date: getNthWeekdayOfMonth(year, 10, 1, 2), name: "Columbus Day" },
    { date: getNthWeekdayOfMonth(year, 11, 4, 4), name: "Thanksgiving Day" }
  );

  // Sort by date
  return holidays.sort((a, b) => a.date.diff(b.date));
};

/**
 * Check if a given date is a working day (not weekend, not holiday)
 * @param {Date|moment|string} date - The date to check
 * @returns {boolean} - True if it's a working day
 */
export const isWorkingDay = (date) => {
  const checkDate = moment(date);
  const dayOfWeek = checkDate.day(); // 0 = Sunday, 6 = Saturday
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const holidayCheck = isUSHoliday(checkDate.toDate());

  return !isWeekend && !holidayCheck.isHoliday;
};

/**
 * Find the previous working day from a given date
 * @param {Date|moment|string} currentDate - The date to start from
 * @param {number} maxDaysToCheck - Maximum days to look back (default: 14)
 * @returns {moment} - The previous working day
 */
export const getPreviousWorkingDay = (currentDate = new Date(), maxDaysToCheck = 14) => {
  const today = moment(currentDate);
  let previousDay = today.clone().subtract(1, 'days');

  let daysChecked = 0;
  while (daysChecked < maxDaysToCheck) {
    if (isWorkingDay(previousDay)) {
      return previousDay;
    }
    previousDay.subtract(1, 'days');
    daysChecked++;
  }

  // Fallback: if no working day found, return 1 day ago
  return today.clone().subtract(1, 'days');
};

/**
 * Calculate smart date range from previous working day until today
 * @param {Date|moment|string} currentDate - The current date (defaults to today)
 * @returns {Object} - { dateFrom: moment, dateTo: moment }
 */
export const getSmartDateRange = (currentDate = new Date()) => {
  const today = moment(currentDate);
  const previousWorkingDay = getPreviousWorkingDay(currentDate);

  const dateFrom = previousWorkingDay.startOf('day');
  const dateTo = today.subtract(1, 'days').endOf('day');

  return { dateFrom, dateTo };
};